import React, { useEffect, useRef, useState } from "react";
import RobotMap from "../../../components/RobotMap/RobotMap";
import { Robot } from "../../../components/RobotMap/models/Robot";
import { DEFAULT_ZOOM_LEVEL } from "../../../components/RobotMap/_icons/3d-model-loader";
import { customLog } from "../../../utils/utils";
import { useListMap } from "../../../hooks/map";
import { center, points } from "@turf/turf";
import { da, fi } from "date-fns/locale";
import { useMe } from "../../../hooks/user";
import { useLocationPoints } from "../../../hooks/location";
import { setCurrentFloor } from "../../../store/map/mapSlice";
import { useDispatch, useSelector } from "react-redux";
import { get } from "http";
import { getRobotImage } from "../mock-robots";
import { DEFAULT_ROBOT_MODEL } from "../../../utils/const";

export interface RobotMapContainerProps {
  onRobotClick: (robot: Robot) => void;
  children?: JSX.Element[];
  line?: [number, number][];
  movement: [number, number][];
  robotData: RobotsData;
  renderLocationMarkers: () => JSX.Element[];
  mapHeight: number
}


interface JobDetails2 {
  compartment_pin: string;
  id: number;
  job_type: string;
  job_id: number;
  organisation_id: string;
  load_compartment: number;
  recipient_id: string;
  recipient_location: string;
  recipient_name: string;
  robot_id: string;
  sender_id: string;
  sender_location: string;
  sender_name: string;
  timestamp: number;
  order_number: string;
  job_status: string;
}

interface RobotPose {
  latitude: number;
  longitude: number;
  orientation: number;
}

interface RobotStatus2 {
  battery: number;
  can_assign_job: boolean;
  is_online: boolean;
  map: string;
  map_uuid?: string;
  layout_uuid?: string;
  organisation: string;
  pose: RobotPose;
  robot_id: string;
  robot_state: string;
  robot_type: string;
  timestamp: number;
  robot_name: string;
  robot_serial_number: string;
}

interface GeneralRobotInfo {
  robot_name: string;
  robot_mac: string;
  robot_serial_number: string;
  robot_type: string;
  organisation: string;
  robot_model?: string;
}

interface RobotInfo {
  robotStatus?: RobotStatus2;
  jobs?: JobData;
  generalInfo: GeneralRobotInfo;
}

interface RobotsData {
  [robot_id: string]: RobotInfo;
}

interface JobData {
  [job_id: string]: JobDetails2;
}


const RobotMapContainer: React.FC<RobotMapContainerProps> = ({
  onRobotClick,
  children,
  line,
  movement,
  robotData,
  renderLocationMarkers,
  mapHeight
}) => {
  const [isMount, setIsMount] = useState(false)
  const [robots, setRobots] = useState<Robot[]>([]);
  const isFallbackLocationUsedRef = useRef(false)

  const dispatch = useDispatch();
  const updatedChildrenRef = useRef<JSX.Element[]>([]);
  // const [lng, setLng] = useState(103.78681142555857);
  // const [lat, setLat] = useState(1.2973538783600418);
  // const [lng, setLng] = useState(103.85845162511345);
  // const [lat, setLat] = useState(1.2822443507516113);


  // const [lng, setLng] = useState<number>(103.83484878754473);
  // const [lat, setLat] = useState(1.2782008843142307);
  const [lng, setLng] = useState<number>();
  const [lat, setLat] = useState<number>();
  //Setting for map overlay
  // const mapOverlaySetting = {
  //   url: "assets/autove-floorplan.png",
  //   topLeftLng: 103.78595945422515,
  //   topLeftLat: 1.2976129210499472,
  //   widthInGIS: 0.0011181175118634928,
  //   heightInGIS: 0.0005332418731876894,
  // };

  const mapOverlaySetting = {
    url: "assets/sgh3.png",
    topLeftLng: 103.83414353971204,
    topLeftLat: 1.2795982810092200,
    widthInGIS: 0.0015776593216401125,
    // heightInGIS: 0.001921218692402249,
    heightInGIS: 0.002292736615798130,
    rotation: 0,
    opacity: 100,
  };



  useEffect(() => {
    customLog("Robot data:", robotData[Object.keys(robotData)[0]]?.robotStatus?.pose);
    const updatedRobots = Object.values(robotData).map(robotDetails => {
      const existingRobot = robots.find(r => r.robotId === robotDetails.robotStatus?.robot_id);
      if (existingRobot && robotDetails.robotStatus) {
        customLog("Updating existing robot")
        // Update existing robot's target position
        existingRobot.updateTarget([
          robotDetails.robotStatus.pose.longitude,
          robotDetails.robotStatus.pose.latitude
        ], robotDetails.robotStatus.pose.orientation);

        existingRobot.batteryLevel = robotDetails.robotStatus.battery;
        existingRobot.status = robotDetails.robotStatus.robot_state;
        existingRobot.robotName = robotDetails.robotStatus.robot_name;
        existingRobot.map_uuid = robotDetails.robotStatus.map_uuid;
        existingRobot.layout_uuid = robotDetails.robotStatus.layout_uuid;
        return existingRobot;
      } else if (robotDetails.robotStatus) {
        // Create new robot
        customLog("Creating new robot");
        return new Robot(
          getRobotImage(robotDetails.generalInfo?.robot_model || DEFAULT_ROBOT_MODEL),
          [robotDetails.robotStatus.pose.longitude, robotDetails.robotStatus.pose.latitude],
          robotDetails.robotStatus
        );
      }
      return null;
    }).filter(Boolean);

    // setRobots(updatedRobots);
    const updatedNewRobots = updatedRobots.filter(robot => robot !== null) as Robot[]
    setRobots(updatedNewRobots);
  }, [robotData]); // Depend on robotData to update robots

  var requestId: number;
  const moveRobots = () => {
    robots.forEach(robot => {
      robot.move(isMount)

      if (!isMount) {
        setIsMount(true)
      }
    });
    requestId = requestAnimationFrame(moveRobots);
  };

  useEffect(() => {
    customLog("Starting animation!!!!!");
    requestId = requestAnimationFrame(moveRobots);
    return () => cancelAnimationFrame(requestId);
  }, [robots]); // Rerun animation when robots update

  const currentFloor = useSelector((state: any) => state.map.currentFloor);

  useEffect(() => {
    if (!children || children.length === 0) {
      return
    }
    if (updatedChildrenRef.current.length !== children.length) {
      updatedChildrenRef.current = children;
      return
    }
    const encryptedNew = children.map(child => Object.values(child.props).join(','));
    const encryptedOld = updatedChildrenRef.current.map(child => Object.values(child.props).join(','));

    if (JSON.stringify(encryptedNew) !== JSON.stringify(encryptedOld)) {
      updatedChildrenRef.current = children;
    }
  }, [children])

  useEffect(() => {
    if (!updatedChildrenRef.current || !children) {
      return;
    }
    updatedChildrenRef.current = children;
  }, [currentFloor])

  const { data: dataMe } = useMe()
  const { data: dataLocation, isLoading } = useLocationPoints();
  const { data: dataMaps, isLoading: isLoadingMap } = useListMap()

  const locations = dataLocation?.data || [];
  const listMaps = dataMaps?.data || []

  const handleFallbackLocation = () => {
    if (listMaps.length > 0) {
      const sortedMap = listMaps.sort((a, b) => a.map_level - b.map_level)
      const firstMap = sortedMap[0]

      dispatch(setCurrentFloor((firstMap.map_level || 3).toString()));

      const centerCoord = center(points([
        [firstMap.map_longitude_tl, firstMap.map_latitude_tl],
        [firstMap.map_longitude_br, firstMap.map_latitude_br]
      ]))
      setLng(centerCoord.geometry.coordinates[0])
      setLat(centerCoord.geometry.coordinates[1])

      return
    }

    setLng(103.83484878754473);
    setLat(1.2782008843142307);
    isFallbackLocationUsedRef.current = true;
  };

  useEffect(() => {
    if (!dataMe || !dataLocation || isLoading || isLoadingMap || !dataMaps) {
      return;
    }

    if (lng !== undefined || lat !== undefined) {
      return
    }
    
    if (locations.length === 0) {
      // Use Fallback location
      handleFallbackLocation();
      return;
    }

    const selectedLocation = locations.find(location => location.id === dataMe.location_id);
    if (!selectedLocation) {
      // Use Fallback location
      handleFallbackLocation();
      return;
    }

    setLng(selectedLocation.pose.longitude);
    setLat(selectedLocation.pose.latitude);
    isFallbackLocationUsedRef.current = false

    // Set Default Current Floor
    if (selectedLocation.map.length > 0) {
      const selectedFloor = selectedLocation.map[0].map_level;
      dispatch(setCurrentFloor(selectedFloor.toString()));
    }

  }, [dataMe, dataLocation, dataMaps])

  return (
    <>
      {lng !== undefined && lat !== undefined && (
        <RobotMap
          startLocation={[lng, lat]}
          isFallbackUsed={isFallbackLocationUsedRef.current}
          zoomLevel={DEFAULT_ZOOM_LEVEL}
          robots={robots}
          line={line}
          onRobotClick={onRobotClick}
          renderLocationMarkers={renderLocationMarkers}
          mapHeight={mapHeight}
        >
          {children}
          {/* {renderLocationMarkers()} */}
        </RobotMap>
      )}
    </>
  );
};

export default RobotMapContainer;